import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Typography,
    message
} from 'antd';
import {
    CopyOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import { tmApi } from '../utils/taskManager';

const { Title, Text } = Typography;

const TaskManagerWorkTypeStagesPage = () => {
    const [loading, setLoading] = useState(false);
    const [workTypes, setWorkTypes] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [stages, setStages] = useState([]);
    const [workType, setWorkType] = useState(null);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [accessRes, stagesRes, statusRes] =
                await Promise.all([
                    tmApi.get(
                        '/api/task-manager/access-constructor'
                    ),
                    tmApi.get(
                        '/api/task-manager/work-type-stages',
                        { params: { all: 1 } }
                    ),
                    tmApi.get('/api/task-manager/statuses')
                ]);
            const types =
                accessRes.data?.data?.work_types || [];
            setWorkTypes(types);
            setStatuses(statusRes.data?.data || []);
            setStages(stagesRes.data?.data || []);
            if (!workType && types.length) {
                setWorkType(types[0].value);
            }
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки этапов'
            );
        } finally {
            setLoading(false);
        }
    }, [workType]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentStages = useMemo(
        () =>
            stages
                .filter(s => s.work_type === workType)
                .sort(
                    (a, b) =>
                        (a.sort_order || 0) -
                            (b.sort_order || 0) ||
                        a.id - b.id
                ),
        [stages, workType]
    );

    const openCreate = () => {
        if (!workType) {
            message.warning('Сначала выберите вид работ');
            return;
        }
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({
            work_type: workType,
            sort_order: (currentStages.length + 1) * 10,
            color: '#2f6fed',
            is_active: true,
            is_initial: false,
            is_final: false
        });
        setOpen(true);
    };

    const openEdit = record => {
        setEditing(record);
        form.setFieldsValue(record);
        setOpen(true);
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                ...values,
                work_type: values.work_type || workType,
                human_name:
                    values.human_name || values.status_name
            };
            if (editing) {
                await tmApi.put(
                    `/api/task-manager/work-type-stages/${editing.id}`,
                    payload
                );
                message.success('Этап обновлён');
            } else {
                await tmApi.post(
                    '/api/task-manager/work-type-stages',
                    payload
                );
                message.success('Этап добавлен');
            }
            setOpen(false);
            await load();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения'
            );
        }
    };

    const remove = async id => {
        try {
            await tmApi.delete(
                `/api/task-manager/work-type-stages/${id}`
            );
            message.success('Этап удалён');
            await load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления'
            );
        }
    };

    const copyFromStatuses = async () => {
        if (!workType) return;
        try {
            const { data } = await tmApi.post(
                '/api/task-manager/work-type-stages/copy-from-statuses',
                { work_type: workType }
            );
            message.success(
                `Скопировано новых: ${data.created || 0}`
            );
            await load();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Не удалось скопировать статусы'
            );
        }
    };

    const usedNames = useMemo(
        () => new Set(currentStages.map(s => s.status_name)),
        [currentStages]
    );

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                    wrap
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Этапы по видам работ
                        </Title>
                        <Text type="secondary">
                            У каждого вида работ свой набор
                            этапов (статусов)
                        </Text>
                    </div>
                    <Button
                        icon={<ReloadOutlined />}
                        onClick={load}
                        loading={loading}
                    >
                        Обновить
                    </Button>
                </Space>

                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Выберите вид работ слева и привяжите к нему этапы. Эти этапы используются в конструкторе прав и маршрутах этапов."
                />

                <Space align="start" wrap style={{ width: '100%' }}>
                    <Card
                        size="small"
                        title="Виды работ"
                        style={{ width: 280 }}
                        loading={loading}
                    >
                        {(workTypes || []).map(wt => {
                            const count = stages.filter(
                                s => s.work_type === wt.value
                            ).length;
                            return (
                                <div
                                    key={wt.id || wt.value}
                                    onClick={() =>
                                        setWorkType(wt.value)
                                    }
                                    style={{
                                        padding: '8px 10px',
                                        marginBottom: 6,
                                        borderRadius: 6,
                                        cursor: 'pointer',
                                        background:
                                            wt.value ===
                                            workType
                                                ? '#e6f4ff'
                                                : '#fafafa',
                                        border:
                                            wt.value ===
                                            workType
                                                ? '1px solid #91caff'
                                                : '1px solid #f0f0f0'
                                    }}
                                >
                                    <Text strong>
                                        {wt.label || wt.value}
                                    </Text>
                                    <div>
                                        <Tag>
                                            этапов: {count}
                                        </Tag>
                                    </div>
                                </div>
                            );
                        })}
                        {!workTypes.length ? (
                            <Text type="secondary">
                                Сначала добавьте виды в
                                справочник «Тип работ»
                            </Text>
                        ) : null}
                    </Card>

                    <Card
                        size="small"
                        style={{ flex: 1, minWidth: 420 }}
                        title={
                            workType
                                ? `Этапы: ${workType}`
                                : 'Выберите вид работ'
                        }
                        extra={
                            workType ? (
                                <Space wrap>
                                    <Button
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={
                                            copyFromStatuses
                                        }
                                    >
                                        Из общих статусов
                                    </Button>
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<PlusOutlined />}
                                        onClick={openCreate}
                                    >
                                        Этап
                                    </Button>
                                </Space>
                            ) : null
                        }
                    >
                        {workType ? (
                            <Table
                                size="small"
                                rowKey="id"
                                loading={loading}
                                pagination={false}
                                dataSource={currentStages}
                                locale={{
                                    emptyText:
                                        'Этапов нет — добавьте или скопируйте из общих статусов'
                                }}
                                columns={[
                                    {
                                        title: 'Цвет',
                                        dataIndex: 'color',
                                        width: 60,
                                        render: c => (
                                            <span
                                                style={{
                                                    display:
                                                        'inline-block',
                                                    width: 16,
                                                    height: 16,
                                                    borderRadius: 4,
                                                    background:
                                                        c ||
                                                        '#ccc'
                                                }}
                                            />
                                        )
                                    },
                                    {
                                        title: 'Код / статус',
                                        dataIndex:
                                            'status_name'
                                    },
                                    {
                                        title: 'Название',
                                        dataIndex: 'human_name'
                                    },
                                    {
                                        title: 'Порядок',
                                        dataIndex: 'sort_order',
                                        width: 80
                                    },
                                    {
                                        title: '',
                                        width: 140,
                                        render: (_, row) => (
                                            <Space>
                                                {row.is_initial ? (
                                                    <Tag color="green">
                                                        старт
                                                    </Tag>
                                                ) : null}
                                                {row.is_final ? (
                                                    <Tag color="red">
                                                        финал
                                                    </Tag>
                                                ) : null}
                                                <Button
                                                    size="small"
                                                    icon={
                                                        <EditOutlined />
                                                    }
                                                    onClick={() =>
                                                        openEdit(
                                                            row
                                                        )
                                                    }
                                                />
                                                <Popconfirm
                                                    title="Удалить этап?"
                                                    onConfirm={() =>
                                                        remove(
                                                            row.id
                                                        )
                                                    }
                                                >
                                                    <Button
                                                        size="small"
                                                        danger
                                                        icon={
                                                            <DeleteOutlined />
                                                        }
                                                    />
                                                </Popconfirm>
                                            </Space>
                                        )
                                    }
                                ]}
                            />
                        ) : (
                            <Text type="secondary">
                                Выберите вид работ слева
                            </Text>
                        )}
                    </Card>
                </Space>

                <Modal
                    open={open}
                    title={
                        editing
                            ? `Этап #${editing.id}`
                            : 'Новый этап для вида работ'
                    }
                    onCancel={() => setOpen(false)}
                    onOk={save}
                    destroyOnClose
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="work_type"
                            label="Вид работ"
                            rules={[{ required: true }]}
                        >
                            <Select
                                disabled={Boolean(editing)}
                                options={workTypes.map(w => ({
                                    value: w.value,
                                    label: w.label || w.value
                                }))}
                            />
                        </Form.Item>
                        <Form.Item
                            name="status_name"
                            label="Статус (код этапа)"
                            rules={[{ required: true }]}
                            extra="Выберите из общих статусов или введите свой код"
                        >
                            <Select
                                showSearch
                                allowClear
                                optionFilterProp="label"
                                placeholder="Выберите или введите свой"
                                options={[
                                    ...statuses
                                        .filter(
                                            s =>
                                                !usedNames.has(
                                                    s.name
                                                ) ||
                                                (editing &&
                                                    editing.status_name ===
                                                        s.name)
                                        )
                                        .map(s => ({
                                            value: s.name,
                                            label: s.name
                                        })),
                                    ...(editing?.status_name &&
                                    !statuses.some(
                                        s =>
                                            s.name ===
                                            editing.status_name
                                    )
                                        ? [
                                              {
                                                  value: editing.status_name,
                                                  label: editing.status_name
                                              }
                                          ]
                                        : [])
                                ]}
                                onSearch={() => {}}
                                onChange={value => {
                                    if (
                                        !form.getFieldValue(
                                            'human_name'
                                        )
                                    ) {
                                        form.setFieldValue(
                                            'human_name',
                                            value
                                        );
                                    }
                                    const st = statuses.find(
                                        s => s.name === value
                                    );
                                    if (st?.color) {
                                        form.setFieldValue(
                                            'color',
                                            st.color
                                        );
                                    }
                                }}
                            />
                        </Form.Item>
                        <Form.Item
                            noStyle
                            shouldUpdate
                        >
                            {() => (
                                <Form.Item
                                    label="Или свой код этапа"
                                >
                                    <Input
                                        placeholder="Например: Выезд на объект"
                                        onBlur={e => {
                                            const v =
                                                e.target.value.trim();
                                            if (!v) return;
                                            form.setFieldsValue({
                                                status_name: v,
                                                human_name:
                                                    form.getFieldValue(
                                                        'human_name'
                                                    ) || v
                                            });
                                        }}
                                    />
                                </Form.Item>
                            )}
                        </Form.Item>
                        <Form.Item
                            name="human_name"
                            label="Отображаемое имя"
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item name="color" label="Цвет">
                            <Input type="color" />
                        </Form.Item>
                        <Form.Item
                            name="sort_order"
                            label="Порядок"
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_initial"
                            label="Начальный"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="is_final"
                            label="Финальный"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активен"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default TaskManagerWorkTypeStagesPage;
